import React from 'react';
import { Maximize2, Minimize2, X } from 'lucide-react';

interface FormModalProps {
  open: boolean;
  title: string;
  description?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  onClose: () => void;
  closeLabel?: string;
  size?: 'md' | 'lg';
  className?: string;
  showExpandToggle?: boolean;
}

const joinClasses = (...classes: Array<string | undefined | false>) =>
  classes.filter(Boolean).join(' ');

const FormModal: React.FC<FormModalProps> = ({
  open,
  title,
  description,
  children,
  footer,
  onClose,
  closeLabel = 'Close form modal',
  size = 'md',
  className,
  showExpandToggle = true,
}) => {
  const [expanded, setExpanded] = React.useState(false);
  const titleId = React.useId();

  React.useEffect(() => {
    if (!open) {
      setExpanded(false);
      return undefined;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, open]);

  if (!open) return null;

  return (
    <div className="form-modal-backdrop" role="presentation">
      <button className="form-modal-scrim" type="button" aria-label={closeLabel} onClick={onClose} />
      <section
        className={joinClasses('form-modal-panel', `form-modal-panel--${size}`, expanded && 'form-modal-panel--expanded', className)}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="form-modal-header">
          <div>
            <h2 id={titleId}>{title}</h2>
            {description ? <p>{description}</p> : null}
          </div>
          <div className="form-modal-header-actions">
            {showExpandToggle ? (
              <button
                className="icon-button"
                type="button"
                aria-label={expanded ? 'Exit fullscreen form' : 'Expand form'}
                onClick={() => setExpanded((value) => !value)}
              >
                {expanded ? <Minimize2 size={18} aria-hidden="true" /> : <Maximize2 size={18} aria-hidden="true" />}
              </button>
            ) : null}
            <button className="icon-button form-modal-close" type="button" aria-label={closeLabel} onClick={onClose}>
              <X size={18} aria-hidden="true" />
            </button>
          </div>
        </div>
        <div className="form-modal-body">
          {children}
        </div>
        {footer ? <div className="form-modal-footer">{footer}</div> : null}
      </section>
    </div>
  );
};

export default FormModal;
